import yoda from '@kibt/bridge';
import { useEffect, useState } from 'react';
import { Dimensions } from 'react-native';
import { useDebounceFn } from 'ahooks';
import { useIsFocused } from './useIsFocused';
import { radar } from '@/common/log';
import { screenHeight } from '@/common/device-info';

export function useDraggableHeight(props: { distance?: number; initFullScreen?: boolean } = {}) {
    const initHeight = Dimensions.get('window').height;
    const { initFullScreen = true } = props;
    const isFouces = useIsFocused();

    const [state, setState] = useState({
        // 新：是否是全屏
        isFullScreen: initFullScreen,
        // 新：兼容useDraggableHeight写法，把几个hooks合并合并
        pageStatus: 'fullScreen' as 'halfScreen' | 'fullScreen',
        // 新：容器高度
        pageHeight: initHeight,
        // 旧：status（1表示半屏，0表示全屏）
        state: 0,
        // 旧：容器高度
        height: initHeight,
        init: false,
    });

    const { run: onContainerHeightChange } = useDebounceFn(
        async (draggableRes = undefined) => {
            if (!isFouces) {
                return;
            }

            let _draggableRes;
            // ios和Android draggable_height回调的参数格式不一样
            try {
                if (draggableRes) _draggableRes = typeof draggableRes === 'object' ? draggableRes : JSON.parse(draggableRes);
            } catch (e) {
                // console.log(e)
            }
            let res;
            if (_draggableRes?.height) {
                // 注意IOS和安卓的callback入参不一样 {"height": 725, "isHalf": "1"} isHalf:1表示半屏
                // draggable_height callback入参 {height: xxx, status: 1, url:xxx} status:1 表示半屏
                res = {
                    height: _draggableRes.height,
                    isHalf: Number(_draggableRes.status ?? _draggableRes.isHalf),
                };
                console.log('[usePageHeight] 0', res);
            } else {
                // 初始化从容器参数获取半屏的高度比例
                res = await yoda.component.isHalfContainerStatus({}); // {"height": 896, "isHalf": 0, "result": 1}
                // @warning: ios在某些机型会返回height: 0
                console.log('[usePageHeight] 1', res);
                if (res?.height === 0) {
                    radar.event({
                        name: 'HalfContainerStatusError',
                        params: {
                            res,
                            screenHeight: screenHeight,
                            p75: screenHeight * 0.75,
                        },
                    });
                }
            }
            const { height = initHeight, isHalf = false } = res || ({} as any);
            if (res.height !== state.pageHeight || !!res.isHalf !== !state.isFullScreen) {
                setState({
                    isFullScreen: !isHalf,
                    pageStatus: isHalf ? 'halfScreen' : 'fullScreen',
                    pageHeight: height,
                    state: isHalf ? 1 : 0,
                    // ios在某些机型会返回height: 0,兜底400高度
                    height: Math.max(height, 400),
                    init: true,
                });
                console.log('[usePageHeight] 容器高度改变，触发setState', isHalf);
            } else {
                if (!state.init)
                    setState((pre) => ({
                        ...pre,
                        init: true,
                    }));
                console.log('[usePageHeight] 容器高度未改变，不触发setState', state);
            }
        },
        {
            wait: 300,
        },
    );

    useEffect(() => {
        // 第一次主动请求
        onContainerHeightChange();

        // 后面增加高度监听
        yoda.event.addEventListener('draggable_height' as any, onContainerHeightChange);
        return () => {
            yoda.event.removeEventListener('draggable_height' as any, onContainerHeightChange);
        };
    }, []);

    return state;
}

/**
 * @deprecated 使用 useDraggableHeight 代替
 */
export function useContainerStatus() {
    const [data, setData] = useState<{ status: 'halfScreen' | 'fullScreen'; height: number }>({
        status: null,
        height: 0,
    });
    const isFouces = useIsFocused();
    const refreshContainerStatus = () => {
        if (!isFouces) {
            return;
        }
        console.log('refreshContainerStatus');
        return yoda.component.isHalfContainerStatus({}).then((res) => {
            const _res = {
                status: res.isHalf ? 'halfScreen' : 'fullScreen',
                height: res.height,
            };
            if (JSON.stringify(_res) !== JSON.stringify(data)) {
                setData({
                    status: res.isHalf ? 'halfScreen' : 'fullScreen',
                    height: res.height,
                });
                console.log('refreshContainerStatus 1', _res);
                return _res;
            }
            console.log('refreshContainerStatus 2', data);
            return data;
        });
    };
    useEffect(() => {
        refreshContainerStatus();
    }, []);
    return {
        ...data,
        refreshContainerStatus,
    };
}
